import type { ConversationResponse, ListConversations200Item, MessageResponse } from '@roko/api/generated/models'
import { collectMessageImages, mapRawCitations } from '@roko/ui/components/chat/citationImages'
import type { TChatListItem, TChatMessage } from '@roko/ui/components/chat/types'

import { getBaseApiURL } from '@/utils/env'

const ASSISTANT_INITIALS = 'AI'
const CHAT_TITLE_MAX_LENGTH = 50
const PLACEHOLDER_CHAT_NAME_PATTERN = /^(Conversation-[a-f0-9]{8}|Chat-[a-f0-9]{8}|Create New Chat|New [Cc]hat)$/

/** List rows may only include id + title (no messages). */
type TConversationListSource = Pick<ConversationResponse, 'id' | 'title'> & Partial<Pick<ConversationResponse, 'messages'>>

export const getUserInitials = (userName?: string | null): string => {
  if (!userName) {
    return 'U'
  }

  const parts = userName.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
  }

  return userName.slice(0, 2).toUpperCase()
}

const parseConversationListItem = (item: ListConversations200Item | TConversationListSource): TConversationListSource => {
  if ('id' in item && 'title' in item && typeof item.id === 'string' && typeof item.title === 'string') {
    return item as TConversationListSource
  }
  const row = item as Record<string, unknown>
  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? 'New chat'),
  }
}

const getConversationDisplayTitle = (conversation: TConversationListSource): string => {
  const firstUserMessage = conversation.messages?.find((message) => message.role === 'user')?.content?.trim()
  if (firstUserMessage) {
    if (firstUserMessage.length <= CHAT_TITLE_MAX_LENGTH) {
      return firstUserMessage
    }

    return `${firstUserMessage.slice(0, CHAT_TITLE_MAX_LENGTH).trim()}...`
  }

  if (PLACEHOLDER_CHAT_NAME_PATTERN.test(conversation.title)) {
    return conversation.title.startsWith('Conversation-') || conversation.title.startsWith('Chat-') ? 'New chat' : conversation.title
  }

  return conversation.title
}

export const mapChatToListItem = (conversation: ListConversations200Item | TConversationListSource): TChatListItem => {
  const source = parseConversationListItem(conversation)
  return {
    id: source.id,
    title: getConversationDisplayTitle(source),
  }
}

const mapMessageToChatMessage = (message: MessageResponse, userInitials: string): TChatMessage => {
  const citations = mapRawCitations(getBaseApiURL(), message.citations)
  return {
    id: message.id,
    role: message.role === 'assistant' ? 'assistant' : 'user',
    authorInitials: message.role === 'assistant' ? ASSISTANT_INITIALS : userInitials,
    content: message.content,
    citations,
    images: collectMessageImages(citations),
  }
}

export const mapChatToMessages = (conversation: ConversationResponse, userInitials: string): TChatMessage[] => {
  return (conversation.messages ?? []).map((message) => mapMessageToChatMessage(message, userInitials))
}
